import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@/hooks/useSession";
import { Button } from "@/components/ui/button";
import { Send, RefreshCw, AlertCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useProfile } from "@/hooks/useProfile";
import { useTrainingProgress } from "@/hooks/useTrainingProgress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DragDropOption {
  id: string;
  text: string;
  correct_category: string;
}

interface DragDropCategory {
  id: string;
  name: string;
}

interface DragDropProps {
  gameId?: string;
  fileId: string;
  resourceId: string;
  onComplete?: () => void;
  isPreview?: boolean;
  onGameUpdate?: (fileId: string, attempt: any) => Promise<void>;
}

export function DragDropGame({ gameId: propGameId, fileId, resourceId, onComplete, isPreview, onGameUpdate }: DragDropProps) {
  const [categories, setCategories] = useState<DragDropCategory[]>([]);
  const [options, setOptions] = useState<DragDropOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draggedOption, setDraggedOption] = useState<DragDropOption | null>(null);
  const [assignedOptions, setAssignedOptions] = useState<{[key: string]: DragDropOption[]}>({});
  const [remainingOptions, setRemainingOptions] = useState<DragDropOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gameId, setGameId] = useState<string | undefined>(propGameId);
  const [passingScore, setPassingScore] = useState<number>(70);
  const [isUpdatingScore, setIsUpdatingScore] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [lastAttempt, setLastAttempt] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showFullError, setShowFullError] = useState(false);
  const { session } = useSession();
  const { toast } = useToast();
  const { data: profile } = useProfile(session?.user?.id);
  const isAdmin = profile?.role === 'admin';
  const { markAsCompleted, isCompleted } = useTrainingProgress(resourceId, fileId);
  
  // Use refs to prevent multiple completion calls
  const completionHandledRef = useRef(false);
  const gameUpdateHandledRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setErrorMessage(null);
      setIsLoading(true);
      setGameCompleted(false);
      setCategories([]);
      setOptions([]);
      setRemainingOptions([]);
      completionHandledRef.current = false;
      gameUpdateHandledRef.current = false;
    };
  }, []);

  // Step 1: Fetch the game ID if not provided
  useEffect(() => {
    const fetchGameId = async () => {
      if (!propGameId) {
        try {
          console.log("Fetching game ID for fileId:", fileId);
          const { data, error } = await supabase
            .from('drag_drop_games')
            .select('id, passing_score')
            .eq('file_id', fileId)
            .single();
            
          if (error) {
            console.error('Error fetching game ID:', error);
            throw error;
          }
          
          if (data) {
            console.log("Found game ID:", data.id);
            setGameId(data.id);
            if (data.passing_score) {
              setPassingScore(data.passing_score);
            }
          } else {
            throw new Error('No game found for this file ID');
          }
        } catch (error) {
          console.error('Error fetching game ID:', error);
          setErrorMessage('Failed to load game data. The game might not exist or there was a connection issue.');
          setIsLoading(false);
        }
      }
    };
    
    if (!propGameId && fileId && !gameId) {
      fetchGameId();
    }
  }, [propGameId, fileId, retryCount, gameId]);

  // Step 2: After we have a gameId, fetch game data
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        if (!gameId) return;
        
        setIsLoading(true);
        setErrorMessage(null);
        
        console.log("Fetching game data for gameId:", gameId);
        
        // Get game metadata (passing score, etc)
        const { data: gameData, error: gameError } = await supabase
          .from('drag_drop_games')
          .select('passing_score, title, description')
          .eq('id', gameId)
          .single();
          
        if (gameError) throw gameError;
        if (gameData?.passing_score) setPassingScore(gameData.passing_score);
        
        // Get categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('drag_drop_categories')
          .select('*')
          .eq('game_id', gameId)
          .order('name');
          
        if (categoriesError) throw categoriesError;
        if (!categoriesData || categoriesData.length === 0) {
          throw new Error('No categories found for this game');
        }
        
        // Get options
        const { data: optionsData, error: optionsError } = await supabase
          .from('drag_drop_options')
          .select('*')
          .eq('game_id', gameId)
          .order('text');
          
        if (optionsError) throw optionsError;
        if (!optionsData || optionsData.length === 0) {
          throw new Error('No options found for this game');
        }
        
        console.log(`Loaded ${categoriesData.length} categories and ${optionsData.length} options`);
        setCategories(categoriesData);
        setOptions(optionsData);
        setRemainingOptions(optionsData);

        // Initialize assigned options
        const initialAssignedOptions: {[key: string]: DragDropOption[]} = {};
        categoriesData.forEach(category => {
          initialAssignedOptions[category.id] = [];
        });
        setAssignedOptions(initialAssignedOptions);
        
        // Check for previous completion
        if (session?.user?.id && !isAdmin) {
          const { data: attemptData, error: attemptError } = await supabase
            .from('drag_drop_attempts')
            .select('*')
            .eq('file_id', fileId)
            .eq('trainee_id', session.user.id)
            .eq('passed', true)
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (attemptError) {
            console.error('Error fetching attempt data:', attemptError);
          } else if (attemptData && attemptData.length > 0) {
            console.log("Found completed attempt:", attemptData[0]);
            setGameCompleted(true);
            setLastAttempt(attemptData[0]);
            
            // Only update progress once when component mounts if game is completed
            if (onGameUpdate && !gameUpdateHandledRef.current && !completionHandledRef.current) {
              gameUpdateHandledRef.current = true;
              completionHandledRef.current = true;
              try {
                await onGameUpdate(fileId, attemptData[0]);
              } catch (err) {
                console.error('Error updating game completion:', err);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching game data:', error);
        const errorMsg = error instanceof Error 
          ? error.message 
          : 'Failed to load game content. Please try refreshing or contact support.';
        setErrorMessage(errorMsg);
        
        toast({
          title: "Error Loading Game",
          description: "Failed to load the drag and drop activity. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (gameId && !gameCompleted) {
      fetchGameData();
    }
  }, [gameId, toast, session, fileId, isAdmin, onGameUpdate, retryCount]);

  const handleDragStart = (option: DragDropOption) => {
    setDraggedOption(option);
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  
  const handleDrop = (e: React.DragEvent, categoryId: string) => {
    e.preventDefault();
    
    if (!draggedOption) return;
    
    let isAlreadyAssigned = false;
    let previousCategoryId: string | null = null;
    
    Object.entries(assignedOptions).forEach(([catId, opts]) => {
      const optionIndex = opts.findIndex(opt => opt.id === draggedOption.id);
      if (optionIndex !== -1) {
        isAlreadyAssigned = true;
        previousCategoryId = catId;
      }
    });
    
    const newAssignedOptions = { ...assignedOptions };
    
    if (isAlreadyAssigned && previousCategoryId) {
      newAssignedOptions[previousCategoryId] = newAssignedOptions[previousCategoryId].filter(
        opt => opt.id !== draggedOption.id
      );
    } else {
      setRemainingOptions(prev => prev.filter(opt => opt.id !== draggedOption.id));
    }
    
    newAssignedOptions[categoryId] = [...newAssignedOptions[categoryId], draggedOption];
    
    setAssignedOptions(newAssignedOptions);
    setDraggedOption(null);
  };
  
  const handleDragEnd = () => {
    setDraggedOption(null);
  };
  
  const returnOptionToPool = (option: DragDropOption, categoryId: string) => {
    const newAssignedOptions = { ...assignedOptions };
    newAssignedOptions[categoryId] = newAssignedOptions[categoryId].filter(
      opt => opt.id !== option.id
    );
    setAssignedOptions(newAssignedOptions);
    
    setRemainingOptions(prev => [...prev, option]);
  };

  const updatePassingScore = async (newScore: number) => {
    if (!gameId || !isAdmin) return;
    
    try {
      setIsUpdatingScore(true);
      
      const { error } = await supabase
        .from('drag_drop_games')
        .update({ passing_score: newScore })
        .eq('id', gameId);
        
      if (error) throw error;
      
      setPassingScore(newScore);
      
      toast({
        title: "Success",
        description: "Passing score updated successfully",
      });
      
    } catch (error) {
      console.error('Error updating passing score:', error);
      toast({
        title: "Error",
        description: "Failed to update passing score",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingScore(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!session?.user?.id || isAdmin) return;
    
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      
      const totalAssigned = Object.values(assignedOptions).reduce(
        (sum, options) => sum + options.length, 0
      );
      
      if (totalAssigned !== options.length) {
        toast({
          title: "Incomplete",
          description: "Please assign all items to categories before submitting",
          variant: "destructive",
        });
        return;
      }
      
      let correctCount = 0;
      
      categories.forEach(category => {
        assignedOptions[category.id].forEach(option => {
          if (option.correct_category === category.name) {
            correctCount++;
          }
        });
      });
      
      const scorePercentage = Math.round((correctCount / options.length) * 100);
      const isPassed = scorePercentage >= passingScore;
      
      const responses = Object.entries(assignedOptions).map(([categoryId, options]) => ({
        categoryId,
        optionIds: options.map(opt => opt.id)
      }));
      
      const attempt = {
        file_id: fileId,
        resource_id: resourceId,
        trainee_id: session.user.id,
        score: scorePercentage,
        passed: isPassed,
        responses: responses
      };
      
      // First record the attempt
      const { error: attemptError } = await supabase
        .from('drag_drop_attempts')
        .insert(attempt);
      
      if (attemptError) throw attemptError;
      
      setLastAttempt({
        ...attempt,
        created_at: new Date().toISOString()
      });
      
      // If the attempt passed, update progress
      if (isPassed) {
        setGameCompleted(true);
        
        try {
          if (!isCompleted && !completionHandledRef.current) {
            completionHandledRef.current = true;
            
            // Use the useTrainingProgress hook to mark as completed
            await markAsCompleted();
            
            toast({
              title: "Success!",
              description: `You scored ${scorePercentage}% and passed the exercise!`,
            });
            
            // Call the onGameUpdate function exactly once
            if (onGameUpdate && !gameUpdateHandledRef.current) {
              gameUpdateHandledRef.current = true;
              console.log("Calling onGameUpdate with fileId:", fileId);
              await onGameUpdate(fileId, attempt);
            }
            
            // Call onComplete exactly once after all updates
            if (onComplete && !gameUpdateHandledRef.current) {
              gameUpdateHandledRef.current = true;
              onComplete();
            }
          }
        } catch (progressError) {
          console.error('Error updating progress:', progressError);
          toast({
            title: "Warning",
            description: "Your score was recorded, but we had trouble updating your progress.",
          });
        }
      } else {
        toast({
          title: "Try Again",
          description: `You scored ${scorePercentage}%. You need ${passingScore}% to pass.`,
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error('Error submitting game:', error);
      setErrorMessage('Failed to submit your answers. Please try again.');
      toast({
        title: "Error",
        description: "Failed to submit your answers",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setIsLoading(true);
    setErrorMessage(null);
    setShowFullError(false);
    
    // Reset the completion flags to try again
    completionHandledRef.current = false;
    gameUpdateHandledRef.current = false;
    
    toast({
      title: "Retrying",
      description: "Attempting to reload the exercise...",
    });
  };
  
  if (errorMessage) {
    return (
      <div className="p-6">
        <Alert variant="destructive" className="mb-8">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load exercise</AlertTitle>
          <AlertDescription>
            {errorMessage}
            {showFullError && (
              <div className="mt-2 text-xs whitespace-pre-wrap font-mono">
                {JSON.stringify(errorMessage, null, 2)}
              </div>
            )}
          </AlertDescription>
        </Alert>
        
        <div className="flex flex-col items-center justify-center gap-4">
          <Button 
            onClick={handleRetry}
            className="flex items-center gap-2"
            variant="default"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
          
          {onComplete && (
            <Button 
              onClick={onComplete}
              className="flex items-center gap-2"
              variant="outline"
            >
              Skip Exercise
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center p-8 h-full">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mb-4"></div>
        <span className="text-gray-600">Loading exercise...</span>
        <p className="text-xs text-gray-400 mt-2">This may take a moment</p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-[calc(100vh-100px)]">
      <div className="p-6 pb-24 relative">
        <h2 className="text-xl font-semibold mb-2">Classification Exercise</h2>
        <p className="text-muted-foreground mb-4">
          Drag each item to its correct category. Try to classify all items correctly.
        </p>
        
        <div className="mb-4 flex items-center justify-between">
          {isAdmin ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#8B5CF6]">Passing score:</span>
              <Select
                value={passingScore?.toString()}
                onValueChange={(value) => updatePassingScore(parseInt(value))}
                disabled={isUpdatingScore}
              >
                <SelectTrigger className="h-8 w-20 text-xs bg-[#F1F0FB] border-[#E5DEFF] text-[#8B5CF6]">
                  <SelectValue placeholder="Score" />
                </SelectTrigger>
                <SelectContent>
                  {[50, 60, 70, 80, 90, 100].map((score) => (
                    <SelectItem key={score} value={score.toString()}>
                      {score}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-sm inline-block px-3 py-1 bg-[#F1F0FB] rounded-full text-[#8B5CF6]">
              Passing score: {passingScore}%
            </p>
          )}
        </div>
        
        {gameCompleted && lastAttempt && !isAdmin && (
          <div className="mb-6 p-4 bg-green-50 border border-green-100 rounded-lg">
            <h3 className="font-medium text-green-800 mb-1">Exercise Completed</h3>
            <p className="text-sm text-green-700">
              Your score: <span className="font-semibold">{lastAttempt.score}%</span>
            </p>
            <p className="text-xs text-green-600 mt-1">
              Completed on: {new Date(lastAttempt.created_at).toLocaleString()}
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border p-4 rounded-lg bg-secondary/20">
            <h3 className="font-medium mb-2">Items to Classify:</h3>
            <ScrollArea className="h-[250px] md:h-[300px] pr-4">
              <div className="flex flex-wrap gap-2">
                {isAdmin ? (
                  <div className="space-y-3 w-full">
                    {options.map(option => (
                      <div 
                        key={option.id}
                        className="bg-white px-3 py-2 rounded-md shadow border flex justify-between items-center"
                      >
                        <span>{option.text}</span>
                        <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          {option.correct_category}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {remainingOptions.map(option => (
                      <div
                        key={option.id}
                        draggable
                        onDragStart={() => handleDragStart(option)}
                        onDragEnd={handleDragEnd}
                        className="bg-white px-3 py-2 rounded-md shadow cursor-move border border-border hover:border-primary transition-colors duration-200"
                      >
                        {option.text}
                      </div>
                    ))}
                    {!remainingOptions.length && (
                      <p className="text-muted-foreground text-sm p-2">
                        All items have been assigned to categories. You can still move them between categories.
                      </p>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
          
          <div className="space-y-6">
            {categories.map(category => (
              <div 
                key={category.id}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, category.id)}
                className="border rounded-lg p-4 transition-colors hover:border-primary/60"
              >
                <h3 className="font-medium mb-3 pb-2 border-b">
                  {category.name}
                </h3>
                {isAdmin ? (
                  <div className="min-h-16 space-y-2">
                    {options
                      .filter(option => option.correct_category === category.name)
                      .map(option => (
                        <div 
                          key={option.id}
                          className="bg-green-50 border border-green-100 px-3 py-2 rounded-md flex items-center"
                        >
                          <span>{option.text}</span>
                        </div>
                      ))}
                    {!options.filter(option => option.correct_category === category.name).length && (
                      <div className="text-muted-foreground text-sm p-2 border border-dashed rounded-md border-muted w-full text-center">
                        No items belong to this category
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="min-h-16 flex flex-wrap gap-2">
                    {assignedOptions[category.id]?.map(option => (
                      <div 
                        key={option.id}
                        className="bg-secondary/50 px-3 py-2 rounded-md flex items-center group"
                      >
                        <span>{option.text}</span>
                        <button
                          onClick={() => returnOptionToPool(option, category.id)}
                          className="ml-2 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {!assignedOptions[category.id]?.length && (
                      <div className="text-muted-foreground text-sm p-2 border border-dashed rounded-md border-muted w-full text-center">
                        Drop items here
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="sticky bottom-0 left-0 right-0 border-t bg-background/80 backdrop-blur-sm p-4 flex justify-center mt-8">
          {isAdmin ? (
            <div className="w-full text-center py-2 px-4 bg-gray-100 rounded-md max-w-md">
              <span className="text-sm text-gray-500">Admin preview mode - showing correct answers</span>
            </div>
          ) : gameCompleted ? (
            <div className="w-full text-center py-2 px-4 bg-green-100 text-green-800 rounded-md max-w-md">
              <span className="text-sm">Exercise completed with score: {lastAttempt?.score}%</span>
            </div>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting || isAdmin || Object.values(assignedOptions).reduce((sum, opts) => sum + opts.length, 0) !== options.length}
              className="w-full max-w-md"
            >
              {isSubmitting ? "Submitting..." : "Submit Answers"}
              <Send className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
